import { IconArrowsDiagonal, IconChevronDown } from "@tabler/icons-react"
import { type ChangeObject, diffWordsWithSpace } from "diff"
import { createContext, type FC, type ReactNode, useContext, useMemo, useState } from "react"

import Tooltip from "@forento/ui/components/Tooltip"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import { DefaultModal, ModalButtons, ModalHeader, ModalInputs, ModalTitle } from "~/components/Modal"

// Rows read this to drop their collapsing and height limits, which only exist to keep the card
// small inside the chat.
const FullscreenContext = createContext(false)

type Props = {
	changeCount: number
	decision: "applied" | "discarded" | null
	isApplying: boolean
	isDiscarding: boolean
	onApply(): void
	onDiscard(): void
	children: ReactNode
}

const AiProposedChanges: FC<Props> = ({
	changeCount,
	decision,
	isApplying,
	isDiscarding,
	onApply,
	onDiscard,
	children,
}) => {
	const [isFullscreen, setFullscreen] = useState(false)

	const decisionPill =
		decision === "applied" ? (
			<span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Applied</span>
		) : decision === "discarded" ? (
			<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Discarded</span>
		) : null

	const decisionButtons = decision === null && (
		<>
			<Button
				variant="secondary"
				isDisabled={isApplying || isDiscarding}
				isLoading={isDiscarding}
				onClick={onDiscard}
			>
				Discard
			</Button>
			<Button variant="primary" isDisabled={isApplying || isDiscarding} isLoading={isApplying} onClick={onApply}>
				Apply changes
			</Button>
		</>
	)

	return (
		<>
			<div className="max-w-[90%] self-start rounded-lg border border-gray-200 bg-white">
				<div className="flex items-center justify-between gap-4 border-b border-gray-200 px-3 py-2">
					<span className="text-sm font-semibold text-gray-900">Proposed changes ({changeCount})</span>
					<div className="flex items-center gap-2">
						{decisionPill}
						<Tooltip tooltip="View full screen">
							<button
								type="button"
								onClick={() => setFullscreen(true)}
								aria-label="View proposed changes full screen"
								className="cursor-pointer rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
							>
								<IconArrowsDiagonal className="size-4" />
							</button>
						</Tooltip>
					</div>
				</div>
				<div className="flex flex-col divide-y divide-gray-100">{children}</div>
				{decision === null && (
					<div className="flex justify-end gap-2 border-t border-gray-200 px-3 py-2">{decisionButtons}</div>
				)}
			</div>
			<DefaultModal onClose={() => setFullscreen(false)} width={1100} isOpen={isFullscreen}>
				<ModalHeader>
					<ModalTitle>Proposed changes ({changeCount})</ModalTitle>
					{decisionPill}
				</ModalHeader>
				<ModalInputs>
					<FullscreenContext.Provider value={true}>
						<div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-200">
							{children}
						</div>
					</FullscreenContext.Provider>
				</ModalInputs>
				<ModalButtons>
					<Button variant="secondary" onClick={() => setFullscreen(false)}>
						Close
					</Button>
					{decisionButtons}
				</ModalButtons>
			</DefaultModal>
		</>
	)
}

export const PlainRow: FC<{ label: string; detail?: string; children?: ReactNode }> = ({ label, detail, children }) => (
	<div className="px-3 py-2">
		<p className="text-sm text-gray-900">{label}</p>
		{detail !== undefined && <p className="text-xs text-gray-500">{detail}</p>}
		{children}
	</div>
)

export const ExpandableRow: FC<{ label: string; detail?: string; children: ReactNode }> = ({
	label,
	detail,
	children,
}) => {
	const isFullscreen = useContext(FullscreenContext)

	if (isFullscreen) {
		return (
			<div className="flex flex-col gap-2 px-4 py-3">
				<div>
					<p className="text-sm font-medium text-gray-900">{label}</p>
					{detail !== undefined && <p className="text-xs text-gray-500">{detail}</p>}
				</div>
				{children}
			</div>
		)
	}

	return (
		<details className="group">
			<summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 [&::-webkit-details-marker]:hidden">
				<span>
					<span className="block text-sm text-gray-900">{label}</span>
					{detail !== undefined && <span className="block text-xs text-gray-500">{detail}</span>}
				</span>
				<IconChevronDown className="size-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180" />
			</summary>
			<div className="px-3 pb-3">{children}</div>
		</details>
	)
}

// Two texts this far apart are a rewrite rather than an edit, and word highlighting on one is
// noise that costs more to compute than it tells you. jsdiff gives up and returns undefined.
const maxDiffEditLength = 2000

export const BeforeAfter: FC<{ before: string | null; after: string }> = ({ before, after }) => {
	const isFullscreen = useContext(FullscreenContext)
	const hasBefore = before !== null && before.trim().length > 0

	// Collapsed rows still render into the DOM, so every proposal would diff on mount without this.
	const changes = useMemo(
		() => (hasBefore ? diffWordsWithSpace(before, after, { maxEditLength: maxDiffEditLength }) : undefined),
		[hasBefore, before, after],
	)

	// Side by side is the point of full screen, but only when there is a before to compare against.
	const isSideBySide = isFullscreen && hasBefore

	return (
		<div className={cn("flex gap-2", isSideBySide ? "flex-col lg:flex-row" : "flex-col")}>
			{hasBefore && (
				<ContentBlock
					tone="removed"
					title="Before"
					isMuted={changes !== undefined}
					className={cn(isSideBySide && "min-w-0 flex-1")}
				>
					{changes !== undefined ? <DiffText changes={changes} side="before" /> : before}
				</ContentBlock>
			)}
			<ContentBlock
				tone="added"
				title="After"
				isMuted={changes !== undefined}
				className={cn(isSideBySide && "min-w-0 flex-1")}
			>
				{changes !== undefined ? <DiffText changes={changes} side="after" /> : after}
			</ContentBlock>
		</div>
	)
}

const DiffText: FC<{ changes: ChangeObject<string>[]; side: "before" | "after" }> = ({ changes, side }) => (
	<>
		{changes.map((change, index) => {
			if (side === "before" ? change.added : change.removed) return null

			const isChanged = change.added === true || change.removed === true
			return (
				<span
					key={index}
					className={cn(
						isChanged
							? side === "before"
								? "bg-red-100 text-red-900"
								: "bg-green-100 text-green-900"
							: "text-gray-400",
					)}
				>
					{change.value}
				</span>
			)
		})}
	</>
)

export const ContentBlock: FC<{
	tone: "added" | "removed"
	title: string
	/** Neutral background, for when the changed words inside carry the colour instead. */
	isMuted?: boolean
	className?: string
	children: ReactNode
}> = ({ tone, title, isMuted = false, className, children }) => {
	const isFullscreen = useContext(FullscreenContext)

	return (
		<div
			className={cn(
				"rounded-md border p-2",
				isMuted
					? "border-gray-200 bg-gray-50"
					: tone === "added"
						? "border-green-100 bg-green-50"
						: "border-red-100 bg-red-50",
				className,
			)}
		>
			<p className={cn("mb-1 text-xs font-medium", tone === "added" ? "text-green-800" : "text-red-800")}>
				{title}
			</p>
			<div
				className={cn(
					"whitespace-pre-wrap text-gray-700",
					isFullscreen ? "text-sm" : "max-h-48 overflow-y-auto text-xs",
				)}
			>
				{children}
			</div>
		</div>
	)
}

export default AiProposedChanges
